import type { AddressInput, AddressParts, AddressVerifyResult } from "@/interfaces/redemption";

/**
 * Client-side Smarty US-street verification (mirrors v1's `verifyAddress`). Called
 * with the WEBSITE key (`key=`), which Smarty host-restricts — so it's browser-safe.
 *
 * Returns a normalized {@link AddressVerifyResult}. Fail-open by design: a missing
 * key, no candidate, or any network/host error resolves to `unverified` so address
 * validation can never trap a recipient (parity with v1's error path).
 */

const US_STREET_URL = "https://us-street.api.smarty.com/street-address";

/** The candidate fields we read from Smarty's response. */
interface SmartyCandidate {
  delivery_line_1?: string;
  components?: {
    city_name?: string;
    state_abbreviation?: string;
    zipcode?: string;
    plus4_code?: string;
  };
}

/**
 * True when Smarty changed something vs what the recipient typed (case-insensitive;
 * a typed 5-digit zip counts as matching Smarty's zip5 or zip+4). Mirrors v1's
 * `checkIfCorrected` — drives whether we show a "did you mean?" suggestion.
 */
function isCorrected(typed: AddressInput, s: AddressParts, zip5: string): boolean {
  const eq = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
  const zipOk = typed.zipCode.trim() === zip5 || typed.zipCode.trim() === s.zip;
  return !(
    eq(typed.address, s.street) &&
    eq(typed.city, s.city) &&
    eq(typed.state, s.state) &&
    zipOk
  );
}

export async function verifyUsAddress(
  typed: AddressInput,
  key: string | null | undefined,
): Promise<AddressVerifyResult> {
  if (!key) return { status: "error" };
  const street = typed.apartment ? `${typed.address} ${typed.apartment}` : typed.address;
  const query = new URLSearchParams({
    street,
    city: typed.city,
    state: typed.state,
    zipcode: typed.zipCode,
    candidates: "1",
    key,
  });

  try {
    const res = await fetch(`${US_STREET_URL}?${query.toString()}`);
    // Non-2xx (e.g. 401/403 on a host not allow-listed for the key) → silent proceed.
    if (!res.ok) return { status: "error" };
    const data = (await res.json()) as SmartyCandidate[];
    if (!Array.isArray(data) || data.length === 0) return { status: "unverified" };

    const c = data[0];
    const zip5 = c?.components?.zipcode ?? "";
    const zip4 = c?.components?.plus4_code ?? "";
    const suggestion: AddressParts = {
      street: c?.delivery_line_1 ?? "",
      city: c?.components?.city_name ?? "",
      state: c?.components?.state_abbreviation ?? "",
      zip: zip4 ? `${zip5}-${zip4}` : zip5,
    };
    // A degenerate candidate (missing core parts) → treat as unverifiable.
    if (!suggestion.street || !suggestion.city || !suggestion.state || !zip5) {
      return { status: "unverified" };
    }
    return isCorrected(typed, suggestion, zip5)
      ? { status: "corrected", suggestion }
      : { status: "verified", suggestion };
  } catch {
    return { status: "error" };
  }
}
